/**
 * Product Guide Modal
 * Opens scent and size guide dialogs and applies the selected option on the product page.
 */

class ProductGuideDialog extends HTMLElement {
  connectedCallback() {
    if (this.isInitialized) return;

    this.handleComponentClick = this.handleComponentClick.bind(this);
    this.handleDialogClick = this.handleDialogClick.bind(this);
    this.handleDialogCancel = this.handleDialogCancel.bind(this);

    this.addEventListener('click', this.handleComponentClick);
    this.bindDialogEvents();

    this.isInitialized = true;
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleComponentClick);

    if (this.boundDialog instanceof HTMLDialogElement) {
      this.boundDialog.removeEventListener('click', this.handleDialogClick);
      this.boundDialog.removeEventListener('cancel', this.handleDialogCancel);
    }

    this.boundDialog = null;
    this.isInitialized = false;
  }

  get dialog() {
    const dialog = this.querySelector('dialog');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  bindDialogEvents() {
    const dialog = this.dialog;
    if (!dialog || dialog === this.boundDialog) return;

    if (this.boundDialog instanceof HTMLDialogElement) {
      this.boundDialog.removeEventListener('click', this.handleDialogClick);
      this.boundDialog.removeEventListener('cancel', this.handleDialogCancel);
    }

    dialog.addEventListener('click', this.handleDialogClick);
    dialog.addEventListener('cancel', this.handleDialogCancel);

    this.boundDialog = dialog;
  }

  handleComponentClick(e) {
    const closeButton = e.target.closest('[data-guide-close], [data-scent-guide-close]');
    if (closeButton) {
      this.closeDialog();
      return;
    }

    const selectButton = e.target.closest('[data-guide-select], [data-scent-guide-select]');
    if (!selectButton) return;

    this.selectGuideOption(
      selectButton.dataset.guideValue || selectButton.dataset.scentValue,
      selectButton.dataset.productId,
      selectButton.dataset.guideKind || 'scent'
    );
  }

  handleDialogClick(e) {
    const dialog = this.dialog;
    if (dialog && e.target === dialog) {
      this.closeDialog();
    }
  }

  handleDialogCancel(e) {
    e.preventDefault();
    this.closeDialog();
  }

  openDialog() {
    this.bindDialogEvents();

    const dialog = this.dialog;
    if (!dialog || dialog.open) return;

    dialog.showModal();
    document.documentElement.setAttribute('scroll-lock', '');
    document.body.style.overflow = 'hidden';
  }

  closeDialog() {
    const dialog = this.dialog;
    if (!dialog || !dialog.open) return;

    dialog.classList.add('dialog-closing');

    const rawSpeed = getComputedStyle(document.documentElement)
      .getPropertyValue('--animation-speed')
      .trim();
    const duration = rawSpeed ? parseFloat(rawSpeed) * 1000 : 250;

    setTimeout(() => {
      dialog.classList.remove('dialog-closing');
      dialog.close();
      document.documentElement.removeAttribute('scroll-lock');
      document.body.style.overflow = '';
    }, duration);
  }

  getValueMatchKeys(value) {
    const base = String(value || '').trim().toLowerCase();

    if (!base) return [];

    const keys = new Set([
      base,
      base.replace(/\s+/g, ''),
      base.replace(/[^a-z0-9]/g, ''),
    ]);

    const withoutParenthetical = base.replace(/\s*\([^)]*\)/g, '').trim();
    if (withoutParenthetical) {
      keys.add(withoutParenthetical);
      keys.add(withoutParenthetical.replace(/\s+/g, ''));
      keys.add(withoutParenthetical.replace(/[^a-z0-9]/g, ''));
    }

    const ounceMatch = base.match(/(\d+(?:\.\d+)?)\s*oz\b/);
    if (ounceMatch) {
      keys.add(`${ounceMatch[1]}oz`);
    }

    const firstToken = withoutParenthetical.split(/\s+/)[0];
    if (firstToken) {
      keys.add(firstToken);
    }

    return [...keys];
  }

  isMatchingGuideValue(candidateValue, guideValue) {
    const candidateKeys = this.getValueMatchKeys(candidateValue);
    const guideKeys = this.getValueMatchKeys(guideValue);

    return candidateKeys.some((key) => guideKeys.includes(key));
  }

  getVariantRadioCandidates(variantPicker, guideKind) {
    const radios = [...variantPicker.querySelectorAll('input[type="radio"]')];
    const keywords =
      guideKind === 'size' ? ['size', 'weight', 'volume'] : ['scent', 'fragrance', 'flavor'];

    const guidedRadios = radios.filter((radio) => {
      const name = radio.name?.toLowerCase() || '';
      return keywords.some((keyword) => name.includes(keyword));
    });

    return guidedRadios.length ? guidedRadios : radios;
  }

  findVariantRadio(variantPicker, guideValue, guideKind) {
    const radioCandidates = this.getVariantRadioCandidates(variantPicker, guideKind);
    return radioCandidates.find((el) => this.isMatchingGuideValue(el.value, guideValue)) || null;
  }

  findPwcOptionButton(productId, guideValue, guideKind) {
    const selector =
      guideKind === 'size'
        ? `.pwc-size-btn[data-size-option][data-product-id="${productId}"]`
        : `.pwc-swatch-btn[data-scent-option][data-product-id="${productId}"]`;

    const buttons = [
      ...document.querySelectorAll(selector),
    ];

    return (
      buttons.find((button) => this.isMatchingGuideValue(button.dataset.value, guideValue)) ||
      buttons.find((button) =>
        this.isMatchingGuideValue(
          button.querySelector('.pwc-size-value, .pwc-swatch-name')?.textContent,
          guideValue
        )
      ) ||
      null
    );
  }

  selectGuideOption(guideValue, productId, guideKind) {
    const variantPicker = document.querySelector(
      `variant-picker[data-product-id="${productId}"]`
    );
    let selectionApplied = false;

    if (variantPicker) {
      const radio = this.findVariantRadio(variantPicker, guideValue, guideKind);

      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        selectionApplied = true;
      } else if (radio?.checked) {
        selectionApplied = true;
      }
    }

    if (!selectionApplied) {
      const pwcOptionButton = this.findPwcOptionButton(productId, guideValue, guideKind);

      if (pwcOptionButton && !pwcOptionButton.classList.contains('selected')) {
        pwcOptionButton.click();
        selectionApplied = true;
      } else if (pwcOptionButton?.classList.contains('selected')) {
        selectionApplied = true;
      }
    }

    if (!selectionApplied) {
      console.warn('[ProductGuide] Could not match guide selection', {
        guideKind,
        productId,
        guideValue,
      });
    }

    this.closeDialog();
  }
}

if (!customElements.get('product-guide-dialog')) {
  customElements.define('product-guide-dialog', ProductGuideDialog);
}

// Use event delegation — no DOMContentLoaded timing issues
if (!window.__productGuideTriggerBound) {
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest(
      '[data-guide-trigger], [data-scent-guide-trigger], [data-size-guide-trigger]'
    );
    if (!trigger) return;

    const guideKind = trigger.dataset.guideKind || (trigger.hasAttribute('data-size-guide-trigger') ? 'size' : 'scent');
    const productId = trigger.dataset.productId;
    const dialogEl = document.getElementById(`${guideKind}-guide-dialog-${productId}`);

    if (dialogEl instanceof ProductGuideDialog) {
      dialogEl.openDialog();
    } else {
      console.warn('[ProductGuide] Could not find guide dialog for product', {
        guideKind,
        productId,
      });
    }
  });

  window.__productGuideTriggerBound = true;
}
